/**
 * Lazy loader для картинок через IntersectionObserver.
 *
 * Использование:
 * <img data-src="image.jpg" data-lazy>
 *
 * Опции через атрибуты:
 * data-src          - основной URL
 * data-low-src      - URL low-res версии (опционально)
 * data-fallback     - URL fallback при ошибке
 * data-root-margin  - кастомный rootMargin
 * data-threshold    - кастомный threshold
 */
export class LazyLoader {

    constructor( options = {} ) {
        this.rootMargin  = options.rootMargin  || '0px 0px 400px 0px';
        this.threshold   = options.threshold   || 0.1;
        this.fallbackSrc = options.fallbackSrc || '';
        this.observer    = null;
    }

    init() {

        if( ! ( 'IntersectionObserver' in window ) ) {
            this.loadAll();
            return;
        }

        this.observer = new IntersectionObserver( ( entries ) => {
            entries.forEach( ( entry ) => {
                if( entry.isIntersecting ) {
                    this.loadImage( entry.target );
                    this.observer.unobserve( entry.target );
                }
            });
        }, {
            rootMargin : this.rootMargin,
            threshold  : this.threshold,
        });

        this.observe();
    }

    /**
     * Подписаться на все img[data-lazy] на странице.
     * Можно вызывать повторно после рендера нового контента.
     */
    observe() {
        document.querySelectorAll( 'img[data-lazy]' ).forEach( ( img ) => {
            this.observer.observe( img );
        });
    }

    loadImage( img ) {

        const src       = img.getAttribute( 'data-src' );
        const lowSrc    = img.getAttribute( 'data-low-src' );
        const mobileSrc = img.getAttribute( 'data-src-mobile' );   // ← новое
        const fallback  = img.getAttribute( 'data-fallback' ) || this.fallbackSrc;

        if( ! src ) {
            img.removeAttribute( 'data-lazy' );
            return;
        }

        const finalSrc = this.pickSrc( src, lowSrc, mobileSrc );

        const onLoad = () => {
            img.classList.add( 'loaded' );
            img.removeAttribute( 'data-lazy' );
            img.removeEventListener( 'load', onLoad );
            img.removeEventListener( 'error', onError );
        };

        const onError = () => {
            if( fallback ) img.src = fallback;
            img.classList.add( 'loaded', 'error' );
            img.removeAttribute( 'data-lazy' );
            img.removeEventListener( 'load', onLoad );
            img.removeEventListener( 'error', onError );
        };

        img.addEventListener( 'load',  onLoad );
        img.addEventListener( 'error', onError );

        img.src = finalSrc;
    }

    /**
     * Выбор source: low-res для медленных соединений, иначе high-res.
     */
    pickSrc( src, lowSrc, mobileSrc = null ) {

        const conn     = navigator.connection;
        const saveData = conn?.saveData;
        const slowConn = conn?.effectiveType && /2g/i.test( conn.effectiveType );
        const isMobile = window.matchMedia( '(max-width: 767px)' ).matches;

        // Приоритет low-res для медленных соединений
        if( ( saveData || slowConn ) && lowSrc )
            return lowSrc;

        // Mobile viewport + есть mobile-картинка
        if( isMobile && mobileSrc )
            return mobileSrc;

        return src;
    }

    loadAll() {
        document.querySelectorAll( 'img[data-lazy]' ).forEach( ( img ) => this.loadImage( img ) );
    }

    destroy() {
        if( this.observer ) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
}